import { useState, useRef, useEffect } from "react";
import { apiPrivate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageCircle, X, Send, ArrowRight, Sparkles, CheckCircle2, Loader2 } from "lucide-react";

/**
 * Floating chatbot widget — visible only for authenticated users in /app area.
 * Flow:
 *   1. Greeting + suggested top FAQ
 *   2. User types question → /api/chatbot/query returns up to 3 matches
 *   3. If no confident match → user can escalate → /api/chatbot/escalate → ref number
 */
export default function ChatbotWidget() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [escalating, setEscalating] = useState(false);
    // messages: {role: 'bot'|'user'|'system', text?, matches?, ref_number?}
    const [messages, setMessages] = useState([
        {
            role: "bot",
            text: `Ciao ${user?.nome || "socio"}! Sono l'assistente virtuale di StartupItalia. Posso aiutarti a trovare risposte tra le FAQ — scrivi pure la tua domanda qui sotto.`,
        },
    ]);
    const [topFaqs, setTopFaqs] = useState([]);
    const scrollerRef = useRef(null);

    // Load few top FAQ as quick suggestions (first time open)
    useEffect(() => {
        if (open && topFaqs.length === 0) {
            apiPrivate.get("/faq")
                .then(r => setTopFaqs((r.data || []).slice(0, 4)))
                .catch(() => {});
        }
    }, [open, topFaqs.length]);

    // Auto-scroll on new message
    useEffect(() => {
        const el = scrollerRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [messages, busy]);

    // Defensive: only socio role sees the widget (also gated at AppLayout)
    if (!user || user.role !== "socio") return null;

    const sendQuery = async (q) => {
        const question = (q ?? input).trim();
        if (!question || busy) return;
        setInput("");
        setMessages(m => [...m, { role: "user", text: question }]);
        setBusy(true);
        try {
            const r = await apiPrivate.post("/chatbot/query", { question });
            const { matches, has_confident_match } = r.data;
            if (matches.length === 0) {
                setMessages(m => [...m, {
                    role: "bot",
                    text: "Non ho trovato risposte tra le FAQ. Vuoi che inoltri la domanda al team Investor Relations? Riceverai risposta entro 2 giorni lavorativi.",
                    showEscalate: true,
                    questionToEscalate: question,
                }]);
            } else {
                setMessages(m => [...m, {
                    role: "bot",
                    text: has_confident_match
                        ? "Ecco cosa ho trovato nelle FAQ:"
                        : "Non sono sicuro al 100%, ma queste FAQ potrebbero essere correlate:",
                    matches,
                    showEscalate: !has_confident_match,
                    questionToEscalate: question,
                }]);
            }
        } catch {
            setMessages(m => [...m, { role: "system", text: "Errore nella ricerca. Riprova tra qualche istante." }]);
        } finally {
            setBusy(false);
        }
    };

    const escalate = async (question) => {
        if (escalating) return;
        setEscalating(true);
        try {
            const r = await apiPrivate.post("/chatbot/escalate", { question });
            setMessages(m => [...m, {
                role: "bot",
                text: `Richiesta inoltrata al team Investor Relations. Riferimento: ${r.data.ref_number}. Riceverai risposta entro 2 giorni lavorativi all'indirizzo ${user?.email}.`,
                ref_number: r.data.ref_number,
            }]);
        } catch {
            setMessages(m => [...m, { role: "system", text: "Errore nell'inoltro. Riprova oppure scrivi una email diretta a info@startupitalia.eu." }]);
        } finally {
            setEscalating(false);
        }
    };

    return (
        <>
            {/* Floating launcher */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="chatbot-launcher"
                    aria-label="Apri assistente"
                    className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full text-white shadow-[0_10px_36px_-8px_rgba(223,70,231,0.55)] bg-[linear-gradient(135deg,#ed4735_0%,#df46e7_55%,#653fcd_100%)] flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
                >
                    <MessageCircle className="h-6 w-6" />
                </button>
            )}

            {/* Panel */}
            {open && (
                <div
                    className="fixed bottom-6 right-6 z-50 w-[min(92vw,400px)] h-[min(78vh,640px)] bg-[#01172d]/95 backdrop-blur-md border border-white/10 rounded-3xl shadow-2xl flex flex-col overflow-hidden"
                    data-testid="chatbot-panel"
                >
                    {/* Header */}
                    <div className="px-5 py-4 bg-[linear-gradient(90deg,#ed4735_0%,#df46e7_50%,#653fcd_100%)] flex items-center justify-between">
                        <div className="flex items-center gap-2.5">
                            <Sparkles className="h-5 w-5 text-white" />
                            <div>
                                <div className="text-white font-display text-base leading-tight" data-testid="chatbot-title">Assistente</div>
                                <div className="text-[10px] uppercase tracking-widest text-white/70 font-grotesk">Investor Relations</div>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)} aria-label="Chiudi" data-testid="chatbot-close" className="text-white/90 hover:text-white">
                            <X className="h-5 w-5" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="chatbot-messages">
                        {messages.map((m, i) => (
                            <MessageBubble key={m.id || `${m.role}-${i}-${(m.text || m.content || "").slice(0, 20)}`} message={m} onEscalate={escalate} escalating={escalating} />
                        ))}

                        {/* Top FAQ quick suggestions, only when conversation is empty (just greeting) */}
                        {messages.length === 1 && topFaqs.length > 0 && (
                            <div className="pt-2" data-testid="chatbot-suggestions">
                                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-grotesk mb-2">Domande frequenti</div>
                                <div className="flex flex-col gap-1.5">
                                    {topFaqs.map((f, i) => (
                                        <button
                                            key={f.id}
                                            onClick={() => sendQuery(f.question)}
                                            data-testid={`chatbot-suggestion-${i}`}
                                            className="text-left text-sm text-zinc-300 hover:text-white border border-white/10 hover:border-bayside/60 hover:bg-bayside/5 rounded-2xl px-3 py-2 transition flex items-center justify-between gap-2 group"
                                        >
                                            <span className="truncate">{f.question}</span>
                                            <ArrowRight className="h-3.5 w-3.5 shrink-0 opacity-0 group-hover:opacity-100 transition" />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {busy && (
                            <div className="flex items-center gap-2 text-zinc-500 text-xs font-grotesk" data-testid="chatbot-typing">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Sto cercando…
                            </div>
                        )}
                    </div>

                    {/* Input */}
                    <form
                        onSubmit={(e) => { e.preventDefault(); sendQuery(); }}
                        className="border-t border-white/10 p-3 flex items-end gap-2 bg-[#01172d]"
                        data-testid="chatbot-form"
                    >
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    sendQuery();
                                }
                            }}
                            placeholder="Scrivi la tua domanda…"
                            rows={1}
                            disabled={busy}
                            data-testid="chatbot-input"
                            className="flex-1 resize-none bg-transparent text-white placeholder-zinc-500 text-sm font-grotesk px-3 py-2 outline-none border border-white/10 focus:border-bayside/60 rounded-2xl max-h-28"
                        />
                        <button
                            type="submit"
                            disabled={busy || !input.trim()}
                            data-testid="chatbot-send"
                            className="h-10 w-10 shrink-0 rounded-full text-white disabled:opacity-40 disabled:cursor-not-allowed bg-[linear-gradient(135deg,#ed4735_0%,#df46e7_55%,#653fcd_100%)] flex items-center justify-center hover:scale-105 transition"
                        >
                            <Send className="h-4 w-4" />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}

function MessageBubble({ message, onEscalate, escalating }) {
    const { role, text, matches, showEscalate, questionToEscalate, ref_number } = message;
    if (role === "user") {
        return (
            <div className="flex justify-end" data-testid="chatbot-user-msg">
                <div className="max-w-[85%] bg-[linear-gradient(135deg,#ed4735_0%,#df46e7_55%,#653fcd_100%)] text-white text-sm font-grotesk px-4 py-2 rounded-2xl rounded-br-md">
                    {text}
                </div>
            </div>
        );
    }
    if (role === "system") {
        return (
            <div className="text-center text-xs text-vermillion font-grotesk py-1" data-testid="chatbot-system-msg">{text}</div>
        );
    }
    // bot
    return (
        <div className="flex flex-col gap-2 max-w-[92%]" data-testid="chatbot-bot-msg">
            {ref_number ? (
                <div className="bg-emerald-500/10 border border-emerald-400/30 text-emerald-200 text-sm font-grotesk px-4 py-3 rounded-2xl rounded-bl-md flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />
                    <span>{text}</span>
                </div>
            ) : (
                <div className="bg-white/5 border border-white/10 text-zinc-200 text-sm font-grotesk px-4 py-2.5 rounded-2xl rounded-bl-md whitespace-pre-line">
                    {text}
                </div>
            )}

            {matches?.length > 0 && (
                <div className="flex flex-col gap-2">
                    {matches.map((m, i) => (
                        <details
                            key={m.id}
                            className="group bg-white/5 border border-white/10 hover:border-bayside/50 rounded-2xl p-3 transition"
                            data-testid={`chatbot-match-${i}`}
                        >
                            <summary className="cursor-pointer list-none flex items-start justify-between gap-2 text-sm font-grotesk font-semibold text-white">
                                <span className="flex-1 leading-snug">{m.question}</span>
                                <span className="text-[10px] uppercase tracking-widest text-bayside opacity-70 mt-0.5 shrink-0">FAQ</span>
                            </summary>
                            <p className="mt-2 text-xs text-zinc-300 leading-relaxed whitespace-pre-line">{m.answer}</p>
                        </details>
                    ))}
                </div>
            )}

            {showEscalate && !ref_number && (
                <button
                    onClick={() => onEscalate(questionToEscalate)}
                    disabled={escalating}
                    data-testid="chatbot-escalate-btn"
                    className="self-start inline-flex items-center gap-2 text-xs font-grotesk uppercase tracking-widest text-bayside hover:text-white border border-bayside/40 hover:bg-bayside/10 px-3 py-2 rounded-full transition disabled:opacity-50"
                >
                    {escalating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
                    Inoltra al team
                </button>
            )}
        </div>
    );
}
